import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

export interface AdvancedSettingsType {
  whisper_model: string;
  use_faster_whisper: boolean;
  max_audio_seconds: number;
  vision_backend: string;
  image_max_width: number;
  inline_assets: boolean;
  debug_level: string;
  no_summary: boolean;
  min_delay: number;
  max_delay: number;
  max_retries: number;
  backoff_multiplier: number;
}

export const defaultAdvancedSettings: AdvancedSettingsType = {
  whisper_model: "small",
  use_faster_whisper: false,
  max_audio_seconds: 0,
  vision_backend: "openai",
  image_max_width: 1920,
  inline_assets: false,
  debug_level: "INFO",
  no_summary: false,
  min_delay: 0.5,
  max_delay: 5.0,
  max_retries: 5,
  backoff_multiplier: 1.5,
};

interface AdvancedSettingsProps {
  settings: AdvancedSettingsType;
  onChange: (settings: AdvancedSettingsType) => void;
}

export const AdvancedSettings = ({ settings, onChange }: AdvancedSettingsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  const updateSetting = <K extends keyof AdvancedSettingsType>(
    key: K,
    value: AdvancedSettingsType[K]
  ) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <CollapsibleTrigger asChild>
        <Button
          variant="outline"
          className="w-full flex items-center justify-between transition-smooth"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="font-semibold">{t("advancedSettings.title")}</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-4 space-y-4 animate-in slide-in-from-top-2">
        <div className="bg-muted/30 rounded-lg p-4 space-y-4">
          {/* Audio Settings */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t("advancedSettings.audioProcessing")}
            </h4>
            
            <div className="space-y-2">
              <Label htmlFor="whisper_model">{t("advancedSettings.whisperModel")}</Label>
              <Select
                value={settings.whisper_model}
                onValueChange={(value) => updateSetting("whisper_model", value)}
              >
                <SelectTrigger id="whisper_model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tiny">{t("advancedSettings.whisperTiny")}</SelectItem>
                  <SelectItem value="base">{t("advancedSettings.whisperBase")}</SelectItem>
                  <SelectItem value="small">{t("advancedSettings.whisperSmall")}</SelectItem>
                  <SelectItem value="medium">{t("advancedSettings.whisperMedium")}</SelectItem>
                  <SelectItem value="large">{t("advancedSettings.whisperLarge")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="use_faster_whisper" className="cursor-pointer">
                {t("advancedSettings.useFasterWhisper")}
              </Label>
              <Switch
                id="use_faster_whisper"
                checked={settings.use_faster_whisper}
                onCheckedChange={(checked) => updateSetting("use_faster_whisper", checked)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_audio_seconds">
                {t("advancedSettings.maxAudioLength")}
              </Label>
              <Input
                id="max_audio_seconds"
                type="number"
                min="0"
                value={settings.max_audio_seconds}
                onChange={(e) => updateSetting("max_audio_seconds", parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Vision Settings */}
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t("advancedSettings.visionAndImages")}
            </h4>
            
            <div className="space-y-2">
              <Label htmlFor="vision_backend">{t("advancedSettings.visionBackend")}</Label>
              <Select
                value={settings.vision_backend}
                onValueChange={(value) => updateSetting("vision_backend", value)}
              >
                <SelectTrigger id="vision_backend">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{t("advancedSettings.visionAuto")}</SelectItem>
                  <SelectItem value="openai">{t("advancedSettings.visionOpenAI")}</SelectItem>
                  <SelectItem value="blip">{t("advancedSettings.visionBLIP")}</SelectItem>
                  <SelectItem value="none">{t("advancedSettings.visionNone")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_max_width">{t("advancedSettings.maxImageWidth")}</Label>
              <Input
                id="image_max_width"
                type="number"
                min="100"
                max="1920"
                value={settings.image_max_width}
                onChange={(e) => updateSetting("image_max_width", parseInt(e.target.value) || 320)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="inline_assets" className="cursor-pointer">
                {t("advancedSettings.inlineAssets")}
              </Label>
              <Switch
                id="inline_assets"
                checked={settings.inline_assets}
                onCheckedChange={(checked) => updateSetting("inline_assets", checked)}
              />
            </div>
          </div>

          {/* Processing Settings */}
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t("advancedSettings.processingOptions")}
            </h4>
            
            <div className="space-y-2">
              <Label htmlFor="debug_level">{t("advancedSettings.debugLevel")}</Label>
              <Select
                value={settings.debug_level}
                onValueChange={(value) => updateSetting("debug_level", value)}
              >
                <SelectTrigger id="debug_level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEBUG">{t("advancedSettings.debugDebug")}</SelectItem>
                  <SelectItem value="INFO">{t("advancedSettings.debugInfo")}</SelectItem>
                  <SelectItem value="WARNING">{t("advancedSettings.debugWarning")}</SelectItem>
                  <SelectItem value="ERROR">{t("advancedSettings.debugError")}</SelectItem>
                  <SelectItem value="CRITICAL">{t("advancedSettings.debugCritical")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="no_summary" className="cursor-pointer">
                {t("advancedSettings.skipSummary")}
              </Label>
              <Switch
                id="no_summary"
                checked={settings.no_summary}
                onCheckedChange={(checked) => updateSetting("no_summary", checked)}
              />
            </div>
          </div>

          {/* Rate Limiting */}
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              {t("advancedSettings.rateLimiting")}
            </h4>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="min_delay">{t("advancedSettings.minDelay")}</Label>
                <Input
                  id="min_delay"
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.min_delay}
                  onChange={(e) => updateSetting("min_delay", parseFloat(e.target.value) || 0.5)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_delay">{t("advancedSettings.maxDelay")}</Label>
                <Input
                  id="max_delay"
                  type="number"
                  step="0.1"
                  min="0"
                  value={settings.max_delay}
                  onChange={(e) => updateSetting("max_delay", parseFloat(e.target.value) || 5.0)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_retries">{t("advancedSettings.maxRetries")}</Label>
                <Input
                  id="max_retries"
                  type="number"
                  min="1"
                  max="20"
                  value={settings.max_retries}
                  onChange={(e) => updateSetting("max_retries", parseInt(e.target.value) || 5)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="backoff_multiplier">{t("advancedSettings.backoffMultiplier")}</Label>
                <Input
                  id="backoff_multiplier"
                  type="number"
                  step="0.1"
                  min="1"
                  max="5"
                  value={settings.backoff_multiplier}
                  onChange={(e) => updateSetting("backoff_multiplier", parseFloat(e.target.value) || 1.5)}
                />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
